import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AppsRoundedIcon from "@mui/icons-material/AppsRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import { Link as RouterLink } from "react-router-dom";
import api from "../bootstrapApi";
import { useShopCart } from "../shopCart";

function CategoryCard({ category, to, fallbackLabel }) {
  return (
    <Button
      component={RouterLink}
      to={to}
      sx={{
        p: 1.2,
        minHeight: 108,
        borderRadius: "22px",
        bgcolor: "#fff",
        border: "1px solid #f0ddd3",
        color: "#3b302f",
        display: "grid",
        gap: 1,
        alignContent: "center",
        justifyItems: "center",
        boxShadow: "0 10px 18px rgba(101,110,143,0.06)",
        textTransform: "none",
      }}
    >
      {category ? (
        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: "16px",
            backgroundImage: category.image_url
              ? `url(${category.image_url})`
              : "linear-gradient(135deg, rgba(232,96,38,0.16), rgba(101,110,143,0.16))",
            backgroundSize: "cover",
            backgroundPosition: "center",
            boxShadow: "inset 0 0 0 1px rgba(226,232,240,0.8)",
          }}
        />
      ) : (
        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: "16px",
            display: "grid",
            placeItems: "center",
            background: "linear-gradient(135deg, rgba(232,96,38,0.16), rgba(101,110,143,0.14))",
            color: "#e86026",
          }}
        >
          <MoreHorizRoundedIcon />
        </Box>
      )}
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.15 }}>
        {category?.name || fallbackLabel}
      </Typography>
    </Button>
  );
}

function DealCard({ product }) {
  return (
    <Box
      sx={{
        p: 1.05,
        borderRadius: "22px",
        bgcolor: "#fff",
        border: "1px solid #f0ddd3",
        boxShadow: "0 10px 18px rgba(101,110,143,0.06)",
        display: "grid",
        gap: 0.85,
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: 118,
          borderRadius: "18px",
          backgroundImage: product?.primary_image
            ? `url(${product.primary_image})`
            : "linear-gradient(135deg, rgba(232,96,38,0.18), rgba(101,110,143,0.16))",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <IconButton
          component={RouterLink}
          to={product ? `/shop/product/${product.id}` : "/shop"}
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            width: 28,
            height: 28,
            bgcolor: "rgba(255,255,255,0.92)",
          }}
        >
          <FavoriteBorderRoundedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#3b302f", lineHeight: 1.2 }}>
        {product?.name || "Produit"}
      </Typography>
      <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#3b302f" }}>
        ${Number(product?.price || 0).toFixed(0)}
      </Typography>
    </Box>
  );
}

export default function ShopHomePage() {
  const accent = "#e86026";
  const support = "#656e8f";
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [message, setMessage] = useState(null);
  const [search, setSearch] = useState("");
  const cartItems = useShopCart();

  useEffect(() => {
    Promise.all([
      api.get("/api/public/categories"),
      api.get("/api/public/products", { params: { limit: 8 } }),
    ])
      .then(([categoriesResponse, productsResponse]) => {
        setCategories(categoriesResponse.data || []);
        setProducts(productsResponse.data || []);
        setMessage(null);
      })
      .catch(() => setMessage({ type: "error", text: "Les categories n'ont pas pu etre chargees." }));
  }, []);

  const filteredCategories = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return categories;
    }

    return categories.filter(
      (category) =>
        category.name?.toLowerCase().includes(query) ||
        category.description?.toLowerCase().includes(query)
    );
  }, [categories, search]);

  const topCategories = filteredCategories.slice(0, 5);
  const featuredProducts = products.slice(0, 4);
  const cartCount = cartItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

  return (
    <Box sx={{ display: "grid", justifyContent: "center" }}>
      {message ? <Alert severity={message.type}>{message.text}</Alert> : null}

      <Box
        sx={{
          width: "100%",
          maxWidth: 430,
          borderRadius: { xs: 0, md: "38px" },
          minHeight: "calc(100vh - 48px)",
          background: "linear-gradient(180deg, #fff7f2 0%, #f7efe9 100%)",
          boxShadow: { xs: "none", md: "0 28px 56px rgba(101,110,143,0.14)" },
          p: 1.9,
          pb: 2.8,
          display: "grid",
          gap: 2.1,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton
            sx={{
              width: 40,
              height: 40,
              bgcolor: "#fff",
              border: "1px solid #f0ddd3",
              boxShadow: "0 8px 16px rgba(101,110,143,0.06)",
            }}
          >
            <AppsRoundedIcon sx={{ fontSize: 20, color: support }} />
          </IconButton>

          <TextField
            placeholder="Rechercher"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: <SearchRoundedIcon style={{ color: support, marginRight: 8 }} />,
              endAdornment: <CameraAltOutlinedIcon style={{ color: support }} />,
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                bgcolor: "#fff",
                borderRadius: "18px",
                boxShadow: "0 8px 16px rgba(101,110,143,0.05)",
              },
            }}
          />

          <IconButton
            component={RouterLink}
            to="/shop/cart"
            sx={{
              width: 40,
              height: 40,
              bgcolor: "#fff",
              border: "1px solid #f0ddd3",
              boxShadow: "0 8px 16px rgba(101,110,143,0.06)",
              position: "relative",
            }}
          >
            <ShoppingBagOutlinedIcon sx={{ fontSize: 20, color: accent }} />
            {cartCount ? (
              <Box
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  minWidth: 17,
                  height: 17,
                  px: 0.4,
                  borderRadius: "999px",
                  bgcolor: accent,
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 800,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                {cartCount}
              </Box>
            ) : null}
          </IconButton>
        </Stack>

        <Box
          sx={{
            borderRadius: "24px",
            px: 2,
            py: 2.1,
            minHeight: 168,
            background: `linear-gradient(135deg, ${accent} 0%, ${support} 100%)`,
            color: "#fff",
            display: "grid",
            gridTemplateColumns: "1fr 0.95fr",
            gap: 1,
            overflow: "hidden",
          }}
        >
          <Box sx={{ display: "grid", alignContent: "start", gap: 0.9 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.02 }}>
              Bei-Tech
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.82)", fontSize: 14, lineHeight: 1.3 }}>
              Des solutions techniques fiables,
              <br />
              elegantes et pensees pour durer.
            </Typography>
            <Stack direction="row" spacing={0.55} sx={{ pt: 0.4 }}>
              {[0, 1, 2, 3].map((dot) => (
                <Box
                  key={dot}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "999px",
                    bgcolor: dot === 0 ? "#fff" : "rgba(255,255,255,0.45)",
                  }}
                />
              ))}
            </Stack>
          </Box>

          <Box
            sx={{
              borderRadius: "20px",
              backgroundImage: "url(/hero.png)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              boxShadow: "0 18px 28px rgba(101,110,143,0.18)",
            }}
          />
        </Box>

        <Box sx={{ display: "grid", gap: 1.1 }}>
          <Typography variant="h6" sx={{ color: "#3b302f", fontWeight: 800 }}>
            Categories
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 1,
            }}
          >
            {topCategories.map((category) => (
              <CategoryCard
                key={category.id}
                category={category}
                to={`/shop/category/${category.id}`}
              />
            ))}
            <CategoryCard fallbackLabel="Plus" to="/shop" />
          </Box>
        </Box>

        <Box sx={{ display: "grid", gap: 1.1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ color: "#3b302f", fontWeight: 800 }}>
              Produits les plus vendus
            </Typography>
            <Button
              component={RouterLink}
              to={categories[0] ? `/shop/category/${categories[0].id}` : "/shop"}
              variant="text"
              sx={{ fontWeight: 700 }}
            >
              Voir tout
            </Button>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 1.1,
            }}
          >
            {featuredProducts.map((product) => (
              <DealCard key={product.id} product={product} />
            ))}
          </Box>
        </Box>

      </Box>
    </Box>
  );
}
